import {
  CognitoUserPool,
  CognitoUser,
  CognitoUserAttribute,
  AuthenticationDetails,
} from 'amazon-cognito-identity-js';

const poolData = {
  UserPoolId: process.env.REACT_APP_COGNITO_USER_POOL_ID,
  ClientId: process.env.REACT_APP_COGNITO_CLIENT_ID,
};

const userPool = new CognitoUserPool(poolData);

// ─── Sign Up ────────────────────────────────
export function signUp({ email, password, invitationCode }) {
  return new Promise((resolve, reject) => {
    const userAttributes = [
      new CognitoUserAttribute({ Name: 'email', Value: email }),
    ];

    const validationData = [
      new CognitoUserAttribute({ Name: 'invitationCode', Value: invitationCode }),
    ];

    userPool.signUp(
      email,
      password,
      userAttributes,
      validationData,
      (err, result) => {
        if (err) return reject(err);
        resolve(result);
      },
      { invitationCode }
    );
  });
}

// ─── Confirm Sign Up ────────────────────────
export function confirmSignUp(email, code) {
  const user = new CognitoUser({ Username: email, Pool: userPool });
  return new Promise((resolve, reject) => {
    user.confirmRegistration(code, true, (err, result) => {
      if (err) return reject(err);
      resolve(result);
    });
  });
}

// ─── Sign In ────────────────────────────────
export function signIn(email, password) {
  const user = new CognitoUser({ Username: email, Pool: userPool });
  const authDetails = new AuthenticationDetails({
    Username: email,
    Password: password,
  });

  return new Promise((resolve, reject) => {
    user.authenticateUser(authDetails, {
      onSuccess: (session) => resolve({ session, user }),
      onFailure: (err) => reject(err),
      mfaRequired: (challengeName, challengeParams) => {
        resolve({ mfaRequired: true, challengeName, challengeParams, user });
      },
      mfaSetup: (challengeName, challengeParams) => {
        resolve({ mfaSetup: true, challengeName, challengeParams, user });
      },
    });
  });
}

// ─── Submit MFA Code ────────────────────────
export function respondToMFAChallenge(user, code, mfaType) {
  return new Promise((resolve, reject) => {
    user.sendMFACode(
      code,
      {
        onSuccess: (session) => resolve({ session, user }),
        onFailure: (err) => reject(err),
      },
      mfaType
    );
  });
}

// ─── Get Current Session ────────────────────
export function getCurrentSession() {
  const user = userPool.getCurrentUser();
  if (!user) return Promise.resolve(null);

  return new Promise((resolve) => {
    user.getSession((err, session) => {
      if (err || !session?.isValid()) return resolve(null);
      resolve({ user, session });
    });
  });
}

// ─── Get Current User Email ─────────────────
export function getCurrentUserEmail() {
  const user = userPool.getCurrentUser();
  if (!user) return Promise.resolve(null);

  return new Promise((resolve) => {
    user.getSession((err) => {
      if (err) return resolve(null);
      user.getUserAttributes((err2, attrs) => {
        if (err2) return resolve(user.getUsername());
        const email = attrs?.find((a) => a.Name === 'email')?.Value;
        resolve(email || user.getUsername());
      });
    });
  });
}

// ─── Get User Custom Attributes ─────────────
export function getUserAttributes() {
  const user = userPool.getCurrentUser();
  if (!user) return Promise.resolve(null);

  return new Promise((resolve) => {
    user.getSession((err) => {
      if (err) return resolve(null);
      user.getUserAttributes((err2, attrs) => {
        if (err2) return resolve(null);
        const get = (name) => attrs?.find((a) => a.Name === name)?.Value;
        resolve({
          email: get('email') || user.getUsername(),
          role: get('custom:role') || 'user',
          status: get('custom:status') || 'active',
          plan: get('custom:plan') || 'free',
        });
      });
    });
  });
}

// ─── Forgot Password ──────────────────────────
export function forgotPassword(email) {
  const user = new CognitoUser({ Username: email, Pool: userPool });
  return new Promise((resolve, reject) => {
    user.forgotPassword({
      onSuccess: (data) => resolve(data),
      onFailure: (err) => reject(err),
    });
  });
}

// ─── Confirm New Password ─────────────────────
export function confirmNewPassword(email, code, newPassword) {
  const user = new CognitoUser({ Username: email, Pool: userPool });
  return new Promise((resolve, reject) => {
    user.confirmPassword(code, newPassword, {
      onSuccess: () => resolve(),
      onFailure: (err) => reject(err),
    });
  });
}

// ─── Sign Out ───────────────────────────────
export function signOut() {
  const user = userPool.getCurrentUser();
  if (user) user.signOut();
}
